import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { Polygon, useMap } from "react-leaflet";
import { connect } from "react-redux";

import { Geometry } from "@/utils";

const POLY_WEIGHT = 3;
const POLY_OPACITY_NEUTRAL = 0.2;
const POLY_OPACITY_HOVER = 0.3;

const LightGroupGeometryInner = ({
    map,
    id,
    vertices,
    group,
    hoveredGroupGeometry,
    ...restProps
}) => {
    const [opacity, setOpacity] = useState(POLY_OPACITY_NEUTRAL);
    const [hover, setHover] = useState(false);

    const onMouseOver = useCallback(() => {
        setOpacity(POLY_OPACITY_HOVER);
    }, []);

    const onMouseOut = useCallback(() => {
        setOpacity(POLY_OPACITY_NEUTRAL);
    }, []);

    const onMouseMove = useCallback(
        (e) => {
            // Note: this is a workaround for the fact that mouse events are not being propagated to lights (possibly due to
            // how the light component is implemented). Instead of using the event framework in react-leaflet, we are
            // ray-casting the mouse position relative to the map to determine whether it is in the polygon.
            const { latlng } = e;
            const position = [+latlng.lat.toFixed(3), +latlng.lng.toFixed(3)];
            const polygon = vertices.map(({ lat, lng }) => [
                +lat.toFixed(3),
                +lng.toFixed(3),
            ]);
            const contains = Geometry.polygonContainsPoint(position, polygon);

            if (contains !== hover) {
                if (!hover) {
                    onMouseOver();
                } else {
                    onMouseOut();
                }

                setHover(contains);
            }
        },
        [vertices, hover, onMouseOver, onMouseOut],
    );

    useEffect(() => {
        map.on("mousemove", onMouseMove);
        return () => {
            map?.off("mousemove", onMouseMove);
        };
    }, [map, onMouseMove]);

    const { color } = group;

    const finalOpacity =
        hoveredGroupGeometry === id ? POLY_OPACITY_HOVER : opacity;

    return (
        <Polygon
            {...restProps}
            positions={vertices}
            pathOptions={{
                weight: POLY_WEIGHT,
                color,
                fill: true,
                fillColor: color,
                fillOpacity: finalOpacity,
            }}
            interactive={false}
        />
    );
};

LightGroupGeometryInner.propTypes = {
    map: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    vertices: PropTypes.array.isRequired,
    group: PropTypes.object.isRequired,
    hoveredGroupGeometry: PropTypes.string,
};

const mapStateToProps = ({ map }) => ({
    hoveredGroupGeometry: map.general.hoveredGroupGeometry,
});

const mapDispatchToProps = {};

const ConnectedLightGroupGeometryInner = connect(
    mapStateToProps,
    mapDispatchToProps,
)(LightGroupGeometryInner);

// Wrapper component to inject the map instance using useMap hook
/**
 * Wrapper injecting the Leaflet map instance into LightGroupGeometryInner for rendering group geometry.
 *
 * @param props - The component props passed to LightGroupGeometryInner.
 * @returns The rendered light group geometry wrapper.
 */
const LightGroupGeometry = (props) => {
    const map = useMap();
    return <ConnectedLightGroupGeometryInner {...props} map={map} />;
};

LightGroupGeometry.POLY_WEIGHT = POLY_WEIGHT;
LightGroupGeometry.POLY_OPACITY_NEUTRAL = POLY_OPACITY_NEUTRAL;
LightGroupGeometry.POLY_OPACITY_HOVER = POLY_OPACITY_HOVER;

export default LightGroupGeometry;
